import { STOP_FADE_MS } from './howler-adapter';
const DEFAULT_VOLUMES = { music: 1, sfx: 1, notification: 1 };
/**
 * The channel an id falls back to when no manifest claims it. `sfx` rather than
 * `music`: an unregistered id is far more likely to be a one-shot, and playing
 * it on the music channel would make it ignore the SFX slider.
 */
const FALLBACK_CHANNEL = 'sfx';
export class AudioService {
    constructor(_adapter, _saveModule, options = {}) {
        this._adapter = _adapter;
        this._saveModule = _saveModule;
        this._muted = {
            music: false,
            sfx: false,
            notification: false,
        };
        this._lastPlayed = new Map();
        this._userInteracted = false;
        /** See {@link setSilenced}. Independent of `_muted` and of the volumes. */
        this._silenced = false;
        /**
         * See {@link setHostMuted}. A **second, independent** hold, deliberately not
         * folded into `_silenced`.
         *
         * WP-50.1: the two have different owners and different lifetimes. The ad
         * controller releases `_silenced` when an ad ends; if platform mute shared
         * that flag, the first ad a muted player watched would hand their audio back —
         * against a requirement the platform states as priority over everything the
         * game itself decides. Two booleans, one quiet.
         */
        this._hostMuted = false;
        /**
         * Module 57 — music asked for while something temporary was in the way.
         *
         * Was `_musicQueue`, entered ONLY when the autoplay gate was shut and drained
         * ONLY by the first gesture. That lost the track outright in every other case:
         * `play()` returns early at volume 0 — which `getVolume()` reports while the
         * platform mute or an ad hold is on — **before** it ever reached the queue, so
         * a player who had CrazyGames muted at load got no music for the whole
         * session, and un-muting could not bring it back because
         * `adapter.setVolume()` is a no-op for a sound that was never constructed.
         *
         * Now every temporary refusal is recorded and every transition that could lift
         * one flushes. Music only, deliberately: an SFX that could not play is moot by
         * the time it could, and a queued click firing thirty seconds later, attached
         * to nothing, is worse than silence.
         */
        this._pendingMusic = [];
        /** Ids already reported through `onUnknownChannel`, so it fires once each. */
        this._warnedUnknown = new Set();
        this._volumes = _saveModule?.load() ?? { ...DEFAULT_VOLUMES };
        this._stopFadeMs = options.stopFadeMs ?? STOP_FADE_MS;
        this._channels = options.channels ?? {};
        this._cooldowns = options.cooldowns ?? {};
        this._liveVolumes = options.volumes;
        this._onVolumeChange = options.onVolumeChange;
        this._excluded =
            options.excluded instanceof Set ? options.excluded : new Set(options.excluded ?? []);
        // Fires immediately when the gesture already happened — which is the whole
        // point of injecting it: by the time this service exists, it usually has.
        options.activation?.onActivated(() => this.onUserInteraction());
        this._onUnknownChannel =
            options.onUnknownChannel ??
                ((audioId) => console.warn(`[audio] "${audioId}" is in no plugin audioManifest — playing on "${FALLBACK_CHANNEL}".`));
    }
    /**
     * WP-37.2: the channel an id plays on, derived from the plugin manifests.
     * Unknown ids fall back to SFX and are reported once, never thrown on — a
     * missing manifest entry must not silence a game in production.
     */
    channelOf(audioId) {
        const map = typeof this._channels === 'function' ? this._channels() : this._channels;
        const channel = map[audioId];
        if (channel !== undefined)
            return channel;
        if (!this._warnedUnknown.has(audioId)) {
            this._warnedUnknown.add(audioId);
            this._onUnknownChannel(audioId);
        }
        return FALLBACK_CHANNEL;
    }
    /**
     * WP-37.1: `options` is passed through to the adapter, so a caller can ask
     * for a looping bed (Water Sort's pour stream, Block Jam's drag). Before
     * this, `AudioService` accepted no options at all — which is part of why the
     * composition root reached past it to `HowlerAdapter` directly.
     *
     * WP-37.2: `channel` is optional and resolved from the manifests when
     * omitted, so `IGameContext.audio.play(key, options)` — which carries no
     * channel — maps straight onto this. An explicit channel still wins.
     */
    /**
     * Module 50 (WP-50.11) — warm a short list of ids so their first play is not
     * late. Silent, and a no-op on an adapter that cannot preload.
     *
     * `Audio_Rules.md` gains the rule this implements: a cue that rides an
     * animation is warmed; a cue that answers a press is lazy. The distinction is
     * not taste — a press-cue's 25–30ms construction is inside the window where
     * the sound still reads as attached to the press, while an animation-cue is
     * committed to a frame it cannot be late for.
     */
    preload(audioIds) {
        // Module 56: an excluded id has no file to warm — `pnpm audio:build` deleted
        // it — so warming one would fetch a 404 during the loading screen, which is
        // the one moment this method exists to keep clear.
        this._adapter.preload?.(audioIds.filter((id) => !this._excluded.has(id)));
    }
    /** Whether this id was excluded in the Audio Studio (Module 56). */
    isExcluded(audioId) {
        return this._excluded.has(audioId);
    }
    play(audioId, channel, options) {
        // Module 56 — FIRST, ahead of `channelOf()`. An excluded cue is not in any
        // manifest's business: resolving its channel first would fire the
        // once-per-id `onUnknownChannel` warn for a cue that is deliberately gone,
        // reporting a shipped decision as a bug. Nothing below this line should ever
        // see an excluded id.
        if (this._excluded.has(audioId))
            return;
        const resolved = channel ?? this.channelOf(audioId);
        // Module 57 — music is OWED rather than dropped. Checked before the
        // individual refusals below, because every one of them is temporary and the
        // track is meant to be playing for as long as the game is on screen.
        if (resolved === 'music' && !this._canPlayMusicNow()) {
            this._owe(audioId, options);
            return;
        }
        if (this._muted[resolved])
            return;
        // WP-37.4: a channel at zero is not played at all, rather than played
        // silently. This preserves what the composition root's hand-rolled wrapper
        // did (`if (sfxVolume > 0)`) and avoids decoding a clip nobody can hear.
        // Still true for SFX; music no longer falls through here, because it is
        // owed above.
        if (this.getVolume(resolved) <= 0)
            return;
        const cooldown = this._cooldowns[audioId] ?? 0;
        if (cooldown > 0) {
            const now = Date.now();
            const last = this._lastPlayed.get(audioId) ?? 0;
            if (now - last < cooldown)
                return;
            this._lastPlayed.set(audioId, now);
        }
        this._adapter.play(audioId, this.getVolume(resolved), options);
    }
    /**
     * WP-37.1: a real stop, fading rather than cutting. This was an empty method
     * body until now — the reason anything needing `stop()` (the pour loop) had
     * to bypass this class entirely.
     *
     * Takes no channel: the adapter keys playback by id alone, and every caller
     * with an id to stop already has it. A track still waiting on the autoplay
     * gate is dropped from the queue rather than left to fire later.
     */
    stop(audioId) {
        // Cancels what is owed as well as what is sounding — leaving the game must
        // not leave a track that starts later, over a screen that is gone.
        const queued = this._pendingMusic.findIndex((q) => q.audioId === audioId);
        if (queued !== -1)
            this._pendingMusic.splice(queued, 1);
        this._adapter.stop(audioId, this._stopFadeMs);
    }
    setVolume(channel, volume) {
        this._volumes[channel] = volume;
        this._saveModule?.save({ ...this._volumes });
        // WP-37.4: when volumes are owned elsewhere (the app's SettingsSaveModule),
        // tell that owner — otherwise this writes to a copy the live provider
        // overwrites on the very next play, which is how setVolume became a no-op
        // in the first place.
        this._onVolumeChange?.(channel, volume);
        // A slider must reach whatever is ALREADY sounding, not just the next
        // play(). Without this, a looping track — started once and never
        // re-triggered again for the rest of the level, e.g. Block Jam's
        // background music — never hears a volume change at all: `getVolume()` is
        // only read at the moment `play()` is called.
        //
        // Not `volume` unconditionally: while either hold is in force the new
        // setting is recorded but must not become audible. For `setSilenced` (an ad
        // is playing) a slider moved behind the ad overlay would otherwise un-silence
        // the game mid-ad; for `setHostMuted` (WP-50.1) the platform's mute is
        // required to outrank the sliders, and this is the line that makes moving one
        // fail to defeat it. Releasing the hold pushes the stored value.
        this._pushLiveVolume(channel, this._silenced || this._hostMuted ? 0 : volume);
        // Module 57 — a player who entered with the music slider at 0 never had a
        // track constructed, so `_pushLiveVolume` above reaches nothing. Raising the
        // slider has to START it. (The stale-`_volumes` case is why the flush reads
        // `getVolume()` again rather than trusting `volume`.)
        this._flushPendingMusic();
    }
    /** Every id this channel has ever played, updated to `volume` in place. A
     *  no-op for any id the adapter has no cached sound for. */
    _pushLiveVolume(channel, volume) {
        const map = typeof this._channels === 'function' ? this._channels() : this._channels;
        for (const [audioId, resolved] of Object.entries(map)) {
            if (resolved === channel)
                this._adapter.setVolume(audioId, volume);
        }
    }
    getVolume(channel) {
        // Either hold silences, and neither can be overridden by the sliders — which
        // is what makes platform mute "take priority over your in-game audio
        // settings" true by construction rather than by every caller remembering.
        if (this._silenced || this._hostMuted)
            return 0;
        return this._settingsVolume(channel);
    }
    /** The player's own volume for a channel, ignoring any temporary silence. */
    _settingsVolume(channel) {
        return this._liveVolumes?.()[channel] ?? this._volumes[channel];
    }
    /**
     * Module 41 (WP-41.10) — silence everything, or stop silencing it, **without
     * touching the player's settings**.
     *
     * The caller is the CrazyGames embed's ad controller: the platform requires a
     * game to be inaudible for the duration of an ad, and audible again the
     * instant it finishes or fails. Named for the effect rather than the cause,
     * because `packages/audio` has no business knowing what an ad is.
     *
     * Why this is not `setVolume(channel, 0)`: `setVolume` persists, through
     * `onVolumeChange` → the app's `SettingsSaveModule`. An ad that started and
     * then failed to call back — or a tab closed mid-ad — would leave the player's
     * saved music and SFX volumes at zero, permanently, with no indication why.
     * Silence here is process state and dies with the page, which is the correct
     * durability for something that is supposed to last thirty seconds.
     *
     * Why not `mute(channel)`: that suppresses future `play()` calls and does
     * nothing to what is already sounding. Block Jam's background music starts
     * once per level and loops, so muting would leave it audible over the ad —
     * exactly the thing the requirement forbids.
     *
     * Idempotent: silencing twice pushes 0 twice, releasing twice restores twice.
     */
    setSilenced(silenced) {
        this._silenced = silenced;
        this._reapplyVolumes();
    }
    /** Whether {@link setSilenced} is currently holding the game quiet. */
    get isSilenced() {
        return this._silenced;
    }
    /**
     * WP-50.1 — the **host platform** has muted the game, or un-muted it.
     *
     * Named for the relationship (`IGameContext.host`) rather than the vendor, for
     * the same reason `setSilenced` is named for its effect: `packages/audio` has no
     * business knowing what CrazyGames is. The caller is the portal composition
     * root, reading `SDK.game.settings.muteAudio` and subscribing to changes.
     *
     * **This outranks the in-game sliders, and cannot be overridden by them.**
     * CrazyGames states it plainly — "This setting should take priority over your
     * in-game audio settings" — so a player who drags the music slider up while the
     * platform is muted hears nothing, and their slider position is still saved and
     * honoured the moment the platform un-mutes. That falls out of `getVolume`
     * returning 0 while this is held: `setVolume` persists the preference and
     * pushes `getVolume`'s answer, not the raw value.
     *
     * Independent of {@link setSilenced} on purpose — see `_hostMuted`. An ad
     * starting and finishing during a platform mute leaves the mute in force.
     *
     * Like `setSilenced`, this is process state: it is re-read from the platform on
     * every boot and deliberately never written to `SettingsSaveModule`, which
     * holds what the *player* chose.
     */
    setHostMuted(muted) {
        this._hostMuted = muted;
        this._reapplyVolumes();
    }
    /** Whether the host platform is currently holding the game quiet. */
    get isHostMuted() {
        return this._hostMuted;
    }
    /**
     * Reach what is ALREADY sounding, per channel, at the volume that channel
     * should now be at. `getVolume` is hold-aware, so this is 0 going in and the
     * player's real volume coming back out — for either hold, without this method
     * needing to know which one moved.
     */
    _reapplyVolumes() {
        for (const channel of ['music', 'sfx', 'notification']) {
            this._pushLiveVolume(channel, this.getVolume(channel));
        }
        // Module 57 — pushing a volume only reaches sounds the adapter has already
        // constructed. A track that was never started because THIS hold was on is
        // not one of them, so releasing the hold has to start it rather than merely
        // turn it up.
        this._flushPendingMusic();
    }
    mute(channel) {
        this._muted[channel] = true;
    }
    unmute(channel) {
        this._muted[channel] = false;
        // Module 57 — un-muting the music channel is one of the five ways an owed
        // track becomes playable.
        if (channel === 'music')
            this._flushPendingMusic();
    }
    onUserInteraction() {
        if (this._userInteracted)
            return;
        this._userInteracted = true;
        // WP-41.4: resume a suspended context first, inside the gesture that got us
        // here. On iOS Safari the owed music below would otherwise be released
        // into a still-suspended context and never be heard. Called unconditionally
        // rather than only in the embed — the main site is served to the same iOS
        // Safari, and resuming a running context is a no-op.
        this._adapter.resume?.();
        this._flushPendingMusic();
    }
    /**
     * Everything that has to be true for a music track to start right now.
     *
     * Module 57 — ONE predicate, read by both the entry (`play()`) and the exit
     * (`_flushPendingMusic()`). Two hand-written copies is the bug this module
     * fixes: `play()` refused at volume 0 and the old drain did not, so a track
     * released while muted started a silent Howl that looped forever.
     */
    _canPlayMusicNow() {
        return this._userInteracted && !this._muted.music && this.getVolume('music') > 0;
    }
    /** Record a music track as owed, once. */
    _owe(audioId, options) {
        if (this._pendingMusic.some((q) => q.audioId === audioId))
            return;
        this._pendingMusic.push({ audioId, options });
    }
    /**
     * Start whatever is owed, if it can start now.
     *
     * Called from every transition that can lift a hold. It is deliberately safe
     * to call when nothing has changed: the predicate decides, so a caller never
     * has to know whether some OTHER hold is still in force.
     */
    _flushPendingMusic() {
        if (this._pendingMusic.length === 0 || !this._canPlayMusicNow())
            return;
        const owed = this._pendingMusic.splice(0, this._pendingMusic.length);
        for (const { audioId, options } of owed) {
            this._adapter.play(audioId, this.getVolume('music'), options);
        }
    }
}
